import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import logger from './utils/logger';

function SettlementInput() {
  const getYesterdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonth = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [settlementDate, setSettlementDate] = useState(getYesterdayDate());
  const [companyOptions, setCompanyOptions] = useState([]);
  const [company, setCompany] = useState('');
  const [totalDepositAmount, setTotalDepositAmount] = useState('');
  const [totalDepositCount, setTotalDepositCount] = useState('');
  const [totalWithdrawalAmount, setTotalWithdrawalAmount] = useState('');
  const [totalWithdrawalCount, setTotalWithdrawalCount] = useState('');
  const [depositFeePerCount, setDepositFeePerCount] = useState('440');
  const [withdrawalFeePerCount, setWithdrawalFeePerCount] = useState('990');
  const [depositFeePerCountMajorBranch, setDepositFeePerCountMajorBranch] = useState('0');
  const [withdrawalFeePerCountMajorBranch, setWithdrawalFeePerCountMajorBranch] = useState('0');
  const [depositFeePerCountBranch, setDepositFeePerCountBranch] = useState('0');
  const [withdrawalFeePerCountBranch, setWithdrawalFeePerCountBranch] = useState('0');
  const [depositFeePerCountJisa, setDepositFeePerCountJisa] = useState('0');
  const [withdrawalFeePerCountJisa, setWithdrawalFeePerCountJisa] = useState('0');
  const [depositFeePerCountDistributor, setDepositFeePerCountDistributor] = useState('0');
  const [withdrawalFeePerCountDistributor, setWithdrawalFeePerCountDistributor] = useState('0');
  const [depositFeePerCountAgency, setDepositFeePerCountAgency] = useState('0');
  const [withdrawalFeePerCountAgency, setWithdrawalFeePerCountAgency] = useState('0');
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingInputs, setLoadingInputs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inputs, setInputs] = useState([]);
  const [listMonth, setListMonth] = useState(getCurrentMonth());
  const [listPage, setListPage] = useState(1);
  const [listLimit] = useState(20);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatNumber = (value) => new Intl.NumberFormat('ko-KR').format(Math.floor(value || 0));

  const formatCurrency = (value) => `${formatNumber(value)}원`;

  const formatLocalDate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateLabel = (value) => {
    if (!value) return '미지정';
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatLocalDate(value);
    }
    if (typeof value === 'string') {
      if (value.includes('T')) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return formatLocalDate(parsed);
        }
      }
      return value.slice(0, 10);
    }
    return String(value);
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/companies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data) ? response.data : [];
      setCompanyOptions(items);
    } catch (err) {
      logger.apiError('GET', '/api/companies', err);
      setError('분류 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchInputs = async () => {
    try {
      setLoadingInputs(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/settlement/inputs/list?month=${listMonth}&page=${listPage}&limit=${listLimit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      const pagination = response.data?.pagination || {};
      setInputs(items);
      setListTotalPages(pagination.totalPages || 0);
      setListTotal(pagination.total || 0);
    } catch (err) {
      logger.apiError('GET', '/api/settlement/inputs', err);
      setError('정산 입력 내역을 불러오지 못했습니다.');
    } finally {
      setLoadingInputs(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchInputs();
  }, [listMonth, listPage]);

  const preventWheelChange = (event) => {
    event.target.blur();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!company) {
      setError('분류를 선택해주세요.');
      return;
    }

    const depositAmount = Number(totalDepositAmount);
    const depositCount = Number(totalDepositCount);
    const withdrawalAmount = Number(totalWithdrawalAmount || 0);
    const withdrawalCount = Number(totalWithdrawalCount);

    if (!Number.isFinite(depositAmount) || depositAmount < 0 ||
        !Number.isFinite(depositCount) || depositCount < 0 ||
        !Number.isFinite(withdrawalAmount) || withdrawalAmount < 0 ||
        !Number.isFinite(withdrawalCount) || withdrawalCount < 0) {
      setError('입력 값은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const existingItem = inputs.find(
        (item) => formatDateLabel(item.settlement_date) === settlementDate && item.company === company
      );
      if (existingItem) {
        setError('해당 날짜와 분류에 이미 입력된 내역이 있습니다. 수정으로 진행해주세요.');
        return;
      }
      const toFee = (v, def) => (Number.isFinite(Number(v)) ? Number(v) : def);
      await axios.post('/api/settlement/manual-input', {
        total_deposit_amount: depositAmount,
        total_deposit_count: depositCount,
        total_withdrawal_amount: withdrawalAmount,
        total_withdrawal_count: withdrawalCount,
        settlement_date: settlementDate,
        company,
        deposit_fee_per_count: toFee(depositFeePerCount, 440),
        withdrawal_fee_per_count: toFee(withdrawalFeePerCount, 990),
        deposit_fee_per_count_major_branch: toFee(depositFeePerCountMajorBranch, 0),
        withdrawal_fee_per_count_major_branch: toFee(withdrawalFeePerCountMajorBranch, 0),
        deposit_fee_per_count_branch: toFee(depositFeePerCountBranch, 0),
        withdrawal_fee_per_count_branch: toFee(withdrawalFeePerCountBranch, 0),
        deposit_fee_per_count_jisa: toFee(depositFeePerCountJisa, 0),
        withdrawal_fee_per_count_jisa: toFee(withdrawalFeePerCountJisa, 0),
        deposit_fee_per_count_distributor: toFee(depositFeePerCountDistributor, 0),
        withdrawal_fee_per_count_distributor: toFee(withdrawalFeePerCountDistributor, 0),
        deposit_fee_per_count_agency: toFee(depositFeePerCountAgency, 0),
        withdrawal_fee_per_count_agency: toFee(withdrawalFeePerCountAgency, 0)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('정산 입력이 저장되었습니다.');

      await fetchInputs();
      setCompany('');
      setTotalDepositAmount('');
      setTotalDepositCount('');
      setTotalWithdrawalAmount('');
      setTotalWithdrawalCount('');
      setDepositFeePerCount('440');
      setWithdrawalFeePerCount('990');
      setDepositFeePerCountMajorBranch('0');
      setWithdrawalFeePerCountMajorBranch('0');
      setDepositFeePerCountBranch('0');
      setWithdrawalFeePerCountBranch('0');
      setDepositFeePerCountJisa('0');
      setWithdrawalFeePerCountJisa('0');
      setDepositFeePerCountDistributor('0');
      setWithdrawalFeePerCountDistributor('0');
      setDepositFeePerCountAgency('0');
      setWithdrawalFeePerCountAgency('0');
    } catch (err) {
      const method = 'POST';
      const endpoint = '/api/settlement/manual-input';
      logger.apiError(method, endpoint, err);
      setError(err?.response?.data?.error || '정산 입력 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingRow({
      id: item.id,
      settlementDate: formatDateLabel(item.settlement_date),
      company: item.company || '',
      totalDepositAmount: String(item.total_deposit_amount ?? ''),
      totalDepositCount: String(item.total_deposit_count ?? ''),
      totalWithdrawalAmount: String(item.total_withdrawal_amount ?? '0'),
      totalWithdrawalCount: String(item.total_withdrawal_count ?? ''),
      depositFeePerCount: String(item.deposit_fee_per_count ?? '440'),
      withdrawalFeePerCount: String(item.withdrawal_fee_per_count ?? '990'),
      depositFeePerCountMajorBranch: String(item.deposit_fee_per_count_major_branch ?? '0'),
      withdrawalFeePerCountMajorBranch: String(item.withdrawal_fee_per_count_major_branch ?? '0'),
      depositFeePerCountBranch: String(item.deposit_fee_per_count_branch ?? '0'),
      withdrawalFeePerCountBranch: String(item.withdrawal_fee_per_count_branch ?? '0'),
      depositFeePerCountJisa: String(item.deposit_fee_per_count_jisa ?? '0'),
      withdrawalFeePerCountJisa: String(item.withdrawal_fee_per_count_jisa ?? '0'),
      depositFeePerCountDistributor: String(item.deposit_fee_per_count_distributor ?? '0'),
      withdrawalFeePerCountDistributor: String(item.withdrawal_fee_per_count_distributor ?? '0'),
      depositFeePerCountAgency: String(item.deposit_fee_per_count_agency ?? '0'),
      withdrawalFeePerCountAgency: String(item.withdrawal_fee_per_count_agency ?? '0')
    });
    setSuccess('');
    setError('');
  };

  const handleEditChange = (field, value) => {
    setEditingRow((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    setError('');
    setSuccess('');

    const depositAmount = Number(editingRow.totalDepositAmount);
    const depositCount = Number(editingRow.totalDepositCount);
    const withdrawalAmount = Number(editingRow.totalWithdrawalAmount || 0);
    const withdrawalCount = Number(editingRow.totalWithdrawalCount);

    if (!Number.isFinite(depositAmount) || depositAmount < 0 ||
        !Number.isFinite(depositCount) || depositCount < 0 ||
        !Number.isFinite(withdrawalAmount) || withdrawalAmount < 0 ||
        !Number.isFinite(withdrawalCount) || withdrawalCount < 0) {
      setError('입력 값은 0 이상의 숫자여야 합니다.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const toFee = (v, def) => (Number.isFinite(Number(v)) ? Number(v) : def);
      await axios.put(`/api/settlement/inputs/${editingRow.id}`, {
        total_deposit_amount: depositAmount,
        total_deposit_count: depositCount,
        total_withdrawal_amount: withdrawalAmount,
        total_withdrawal_count: withdrawalCount,
        settlement_date: editingRow.settlementDate,
        company: editingRow.company,
        deposit_fee_per_count: toFee(editingRow.depositFeePerCount, 440),
        withdrawal_fee_per_count: toFee(editingRow.withdrawalFeePerCount, 990),
        deposit_fee_per_count_major_branch: toFee(editingRow.depositFeePerCountMajorBranch, 0),
        withdrawal_fee_per_count_major_branch: toFee(editingRow.withdrawalFeePerCountMajorBranch, 0),
        deposit_fee_per_count_branch: toFee(editingRow.depositFeePerCountBranch, 0),
        withdrawal_fee_per_count_branch: toFee(editingRow.withdrawalFeePerCountBranch, 0),
        deposit_fee_per_count_jisa: toFee(editingRow.depositFeePerCountJisa, 0),
        withdrawal_fee_per_count_jisa: toFee(editingRow.withdrawalFeePerCountJisa, 0),
        deposit_fee_per_count_distributor: toFee(editingRow.depositFeePerCountDistributor, 0),
        withdrawal_fee_per_count_distributor: toFee(editingRow.withdrawalFeePerCountDistributor, 0),
        deposit_fee_per_count_agency: toFee(editingRow.depositFeePerCountAgency, 0),
        withdrawal_fee_per_count_agency: toFee(editingRow.withdrawalFeePerCountAgency, 0)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('정산 입력이 수정되었습니다.');
      setEditingRow(null);
      await fetchInputs();
    } catch (err) {
      logger.apiError('PUT', '/api/settlement/inputs/:id', err);
      setError(err?.response?.data?.error || '정산 입력 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmDelete = window.confirm('정산 입력을 삭제할까요?');
    if (!confirmDelete) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/settlement/inputs/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingRow?.id === item.id) {
        setEditingRow(null);
      }
      setSuccess('정산 입력이 삭제되었습니다.');
      await fetchInputs();
    } catch (err) {
      logger.apiError('DELETE', '/api/settlement/inputs/:id', err);
      setError(err?.response?.data?.error || '정산 입력 삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
  };

  const groupedInputs = useMemo(() => {
    const sorted = [...inputs].sort((a, b) => {
      if (a.settlement_date === b.settlement_date) {
        return b.id - a.id;
      }
      return a.settlement_date < b.settlement_date ? 1 : -1;
    });
    const groups = new Map();
    sorted.forEach((item) => {
      const key = formatDateLabel(item.settlement_date);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    });
    return Array.from(groups.entries());
  }, [inputs]);

  const handleMonthChange = (event) => {
    setListMonth(event.target.value);
    setListPage(1);
  };

  return (
    <div className="settlement-input-layout">
      <div className="settlement-input-card">
        <h1>신규 정산 입력</h1>
        <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="settlementDate">정산 날짜</label>
          <input
            id="settlementDate"
            type="date"
            value={settlementDate}
            onChange={(event) => setSettlementDate(event.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="companySelect">분류</label>
          <select
            id="companySelect"
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            disabled={loadingCompanies}
          >
            <option value="">분류 선택</option>
            {companyOptions.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="totalDepositAmount">총 입금액</label>
          <input
            id="totalDepositAmount"
            type="number"
            min="0"
            step="1"
            value={totalDepositAmount}
            onChange={(event) => setTotalDepositAmount(event.target.value)}
            onWheel={preventWheelChange}
            placeholder="총 입금액을 입력하세요"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="totalDepositCount">총 입금건수</label>
          <input
            id="totalDepositCount"
            type="number"
            min="0"
            step="1"
            value={totalDepositCount}
            onChange={(event) => setTotalDepositCount(event.target.value)}
            onWheel={preventWheelChange}
            placeholder="총 입금건수를 입력하세요"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="totalWithdrawalAmount">총 출금액</label>
          <input
            id="totalWithdrawalAmount"
            type="number"
            min="0"
            step="1"
            value={totalWithdrawalAmount}
            onChange={(event) => setTotalWithdrawalAmount(event.target.value)}
            onWheel={preventWheelChange}
            placeholder="총 출금액을 입력하세요"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="totalWithdrawalCount">총 출금건수</label>
          <input
            id="totalWithdrawalCount"
            type="number"
            min="0"
            step="1"
            value={totalWithdrawalCount}
            onChange={(event) => setTotalWithdrawalCount(event.target.value)}
            onWheel={preventWheelChange}
            placeholder="총 출금건수를 입력하세요"
            required
          />
        </div>

        <div className="form-group fee-per-count-section">
          <label>입출금 건당 수수료 (원)</label>
          <div className="fee-per-count-grid">
            <div className="fee-per-count-block">
              <span className="fee-role-label">본사</span>
              <input type="number" min="0" step="1" value={depositFeePerCount} onChange={(e) => setDepositFeePerCount(e.target.value)} onWheel={preventWheelChange} placeholder="입금 440" title="입금건당" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCount} onChange={(e) => setWithdrawalFeePerCount(e.target.value)} onWheel={preventWheelChange} placeholder="출금 990" title="출금건당" />
            </div>
            <div className="fee-per-count-block">
              <span className="fee-role-label">대본사</span>
              <input type="number" min="0" step="1" value={depositFeePerCountMajorBranch} onChange={(e) => setDepositFeePerCountMajorBranch(e.target.value)} onWheel={preventWheelChange} placeholder="입금 0" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCountMajorBranch} onChange={(e) => setWithdrawalFeePerCountMajorBranch(e.target.value)} onWheel={preventWheelChange} placeholder="출금 0" />
            </div>
            <div className="fee-per-count-block">
              <span className="fee-role-label">부본사</span>
              <input type="number" min="0" step="1" value={depositFeePerCountBranch} onChange={(e) => setDepositFeePerCountBranch(e.target.value)} onWheel={preventWheelChange} placeholder="입금 0" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCountBranch} onChange={(e) => setWithdrawalFeePerCountBranch(e.target.value)} onWheel={preventWheelChange} placeholder="출금 0" />
            </div>
            <div className="fee-per-count-block">
              <span className="fee-role-label">지사</span>
              <input type="number" min="0" step="1" value={depositFeePerCountJisa} onChange={(e) => setDepositFeePerCountJisa(e.target.value)} onWheel={preventWheelChange} placeholder="입금 0" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCountJisa} onChange={(e) => setWithdrawalFeePerCountJisa(e.target.value)} onWheel={preventWheelChange} placeholder="출금 0" />
            </div>
            <div className="fee-per-count-block">
              <span className="fee-role-label">총판</span>
              <input type="number" min="0" step="1" value={depositFeePerCountDistributor} onChange={(e) => setDepositFeePerCountDistributor(e.target.value)} onWheel={preventWheelChange} placeholder="입금 0" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCountDistributor} onChange={(e) => setWithdrawalFeePerCountDistributor(e.target.value)} onWheel={preventWheelChange} placeholder="출금 0" />
            </div>
            <div className="fee-per-count-block">
              <span className="fee-role-label">매장</span>
              <input type="number" min="0" step="1" value={depositFeePerCountAgency} onChange={(e) => setDepositFeePerCountAgency(e.target.value)} onWheel={preventWheelChange} placeholder="입금 0" />
              <input type="number" min="0" step="1" value={withdrawalFeePerCountAgency} onChange={(e) => setWithdrawalFeePerCountAgency(e.target.value)} onWheel={preventWheelChange} placeholder="출금 0" />
            </div>
          </div>
          <span className="fee-hint">각 역할별 입금건당 / 출금건당 수수료</span>
        </div>

        <button className="submit-btn" type="submit" disabled={submitting}>
          {submitting ? '저장 중...' : '정산 입력 저장'}
        </button>
        </form>

        {error && (
          <div className="error">
            <h3>오류</h3>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="result">
            <h3>완료</h3>
            <p>{success}</p>
          </div>
        )}
      </div>

      <div className="settlement-input-card">
        <div className="settlement-input-list-header">
          <div>
            <h2>입력 내역</h2>
            <div className="settlement-input-list-meta">
              총 {formatNumber(listTotal)}건
            </div>
          </div>
          <div className="settlement-input-list-controls">
            <label htmlFor="inputMonth">월 선택</label>
            <input
              id="inputMonth"
              type="month"
              value={listMonth}
              onChange={handleMonthChange}
            />
          </div>
        </div>
        {loadingInputs ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>불러오는 중...</div>
        ) : groupedInputs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>입력 내역이 없습니다.</div>
        ) : (
          <table className="deposit-table">
            <thead>
              <tr>
                <th>분류</th>
                <th>총 입금액</th>
                <th>총 입금건수</th>
                <th>총 출금액</th>
                <th>총 출금건수</th>
                <th>역할별 입/출금건당 수수료</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {groupedInputs.map(([dateKey, items]) => (
                <React.Fragment key={dateKey}>
                  <tr className="settlement-input-group-row">
                    <td colSpan={7}>정산일: {dateKey}</td>
                  </tr>
                  {items.map((item) => {
                    const isEditing = editingRow?.id === item.id;
                    return (
                      <tr key={item.id}>
                        <td>{item.company}</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editingRow.totalDepositAmount}
                              onChange={(event) => handleEditChange('totalDepositAmount', event.target.value)}
                              onWheel={preventWheelChange}
                              className="settlement-inline-input"
                            />
                          ) : (
                            formatCurrency(item.total_deposit_amount)
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editingRow.totalDepositCount}
                              onChange={(event) => handleEditChange('totalDepositCount', event.target.value)}
                              onWheel={preventWheelChange}
                              className="settlement-inline-input"
                            />
                          ) : (
                            `${formatNumber(item.total_deposit_count)}건`
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editingRow.totalWithdrawalAmount}
                              onChange={(event) => handleEditChange('totalWithdrawalAmount', event.target.value)}
                              onWheel={preventWheelChange}
                              className="settlement-inline-input"
                            />
                          ) : (
                            formatCurrency(item.total_withdrawal_amount)
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={editingRow.totalWithdrawalCount}
                              onChange={(event) => handleEditChange('totalWithdrawalCount', event.target.value)}
                              onWheel={preventWheelChange}
                              className="settlement-inline-input"
                            />
                          ) : (
                            `${formatNumber(item.total_withdrawal_count)}건`
                          )}
                        </td>
                        <td className="fee-per-count-cell">
                          {isEditing ? (
                            <div className="fee-edit-grid">
                              {[
                                ['본사', 'depositFeePerCount', 'withdrawalFeePerCount'],
                                ['대본사', 'depositFeePerCountMajorBranch', 'withdrawalFeePerCountMajorBranch'],
                                ['부본사', 'depositFeePerCountBranch', 'withdrawalFeePerCountBranch'],
                                ['지사', 'depositFeePerCountJisa', 'withdrawalFeePerCountJisa'],
                                ['총판', 'depositFeePerCountDistributor', 'withdrawalFeePerCountDistributor'],
                                ['매장', 'depositFeePerCountAgency', 'withdrawalFeePerCountAgency']
                              ].map(([label, depKey, wdrawKey]) => (
                                <div key={label} className="fee-edit-block">
                                  <span>{label}</span>
                                  <input type="number" min="0" step="1" value={editingRow[depKey]} onChange={(e) => handleEditChange(depKey, e.target.value)} onWheel={preventWheelChange} placeholder="입" title="입금건당" />
                                  <input type="number" min="0" step="1" value={editingRow[wdrawKey]} onChange={(e) => handleEditChange(wdrawKey, e.target.value)} onWheel={preventWheelChange} placeholder="출" title="출금건당" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="fee-summary">
                              본사 {formatNumber(item.deposit_fee_per_count ?? 440)}/{formatNumber(item.withdrawal_fee_per_count ?? 990)} ·
                              대본사 {formatNumber(item.deposit_fee_per_count_major_branch ?? 0)}/{formatNumber(item.withdrawal_fee_per_count_major_branch ?? 0)} ·
                              부본사 {formatNumber(item.deposit_fee_per_count_branch ?? 0)}/{formatNumber(item.withdrawal_fee_per_count_branch ?? 0)} ·
                              지사 {formatNumber(item.deposit_fee_per_count_jisa ?? 0)}/{formatNumber(item.withdrawal_fee_per_count_jisa ?? 0)} ·
                              총판 {formatNumber(item.deposit_fee_per_count_distributor ?? 0)}/{formatNumber(item.withdrawal_fee_per_count_distributor ?? 0)} ·
                              매장 {formatNumber(item.deposit_fee_per_count_agency ?? 0)}/{formatNumber(item.withdrawal_fee_per_count_agency ?? 0)}
                            </span>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="settlement-edit-actions">
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                className="settlement-save-button"
                                disabled={submitting}
                              >
                                저장
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="settlement-cancel-button"
                                disabled={submitting}
                              >
                                취소
                              </button>
                            </div>
                          ) : (
                            <div className="settlement-edit-actions">
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="settlement-edit-button"
                              >
                                수정
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(item)}
                                className="settlement-delete-button"
                              >
                                삭제
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        {listTotalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.max(1, prev - 1))}
              disabled={listPage <= 1}
            >
              이전
            </button>
            <button type="button" disabled>
              {listPage} / {listTotalPages}
            </button>
            <button
              type="button"
              onClick={() => setListPage((prev) => Math.min(listTotalPages, prev + 1))}
              disabled={listPage >= listTotalPages}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SettlementInput;
